/**
 * CostEditModal — a focused, lightweight cost-breakdown editor.
 *
 * Used from the FinancialsPage so the user can quickly update line items
 * (which alter total costs and the Net column) without going through the
 * full "Edit Project" modal.
 */
import { useState } from "react";
import {
  X, Plus, Trash, DotsSixVertical, ArrowsClockwise,
} from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const TAG_SUGGESTIONS = [
  "Materials", "Labour", "Subcontractor", "Equipment", "Permits & Fees",
  "Engineering", "Travel", "Software", "Other",
];

function emptyRow() {
  return { label: "", amount: "", notes: "", tag: "" };
}

export default function CostEditModal({ project, onClose, onSave }) {
  const [items, setItems] = useState(
    (project.costs || []).map((c) => ({
      label:  c.label  ?? "",
      amount: c.amount == null ? "" : String(c.amount),
      notes:  c.notes  ?? "",
      tag:    c.tag    ?? "",
    }))
  );
  const [saving, setSaving] = useState(false);

  const [dragIdx, setDragIdx] = useState(null);
  const [dropIdx, setDropIdx] = useState(null);

  const total = items.reduce((s, c) => {
    const n = parseFloat(c.amount);
    return s + (Number.isFinite(n) ? n : 0);
  }, 0);

  const addRow    = () => setItems([...items, emptyRow()]);
  const updateRow = (i, field, value) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: value };
    setItems(next);
  };
  const removeRow = (i) => setItems(items.filter((_, idx) => idx !== i));

  const onDragStart = (i) => (e) => {
    setDragIdx(i);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(i));
  };
  const onDragOver = (i) => (e) => { e.preventDefault(); if (dropIdx !== i) setDropIdx(i); };
  const onDrop     = (i) => (e) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); setDropIdx(null); return; }
    const next = [...items];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved);
    setItems(next);
    setDragIdx(null); setDropIdx(null);
  };
  const onDragEnd = () => { setDragIdx(null); setDropIdx(null); };

  const handleSave = async () => {
    const cleaned = items
      .map((c) => ({
        label: (c.label || "").trim(),
        amount: c.amount === "" ? 0 : (Number.isFinite(parseFloat(c.amount)) ? parseFloat(c.amount) : 0),
        notes: (c.notes || "").trim(),
        tag:   (c.tag   || "").trim(),
      }))
      .filter((c) => c.label || c.amount || c.notes || c.tag);
    setSaving(true);
    try {
      await onSave(cleaned);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-12 overflow-y-auto" data-testid="cost-edit-modal">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative z-10 bg-white border border-slate-200 rounded-sm shadow-xl max-w-3xl w-full">
        <header className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="font-cabinet text-base font-bold text-slate-900 truncate max-w-md">{project.name}</h2>
            <p className="text-xs text-slate-500">Edit cost breakdown</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="cost-edit-close">
            <X size={16} />
          </button>
        </header>

        <div className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Line Items</Label>
            <Button type="button" variant="outline" size="sm" onClick={addRow} className="h-7 px-2 text-xs" data-testid="cost-edit-add-row">
              <Plus size={12} className="mr-1" /> Add line item
            </Button>
          </div>

          <datalist id="cost-edit-tag-suggestions">
            {TAG_SUGGESTIONS.map((t) => <option key={t} value={t} />)}
          </datalist>

          {items.length === 0 ? (
            <p className="text-xs text-slate-400 italic py-6 text-center">
              No cost items yet. Click "Add line item" to start.
            </p>
          ) : (
            <div className="space-y-1.5">
              {items.map((row, i) => {
                const isDragging = dragIdx === i;
                const isDropTarget = dropIdx === i && dragIdx !== null && dragIdx !== i;
                return (
                  <div
                    key={i}
                    draggable
                    onDragStart={onDragStart(i)}
                    onDragOver={onDragOver(i)}
                    onDragLeave={() => setDropIdx(null)}
                    onDrop={onDrop(i)}
                    onDragEnd={onDragEnd}
                    className={`grid grid-cols-12 gap-2 items-center transition-all ${
                      isDragging ? "opacity-40" : ""
                    } ${isDropTarget ? "border-t-2 border-primary pt-1" : ""}`}
                    data-testid={`cost-edit-row-${i}`}
                  >
                    <div className="col-span-1 flex items-center justify-center text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing">
                      <DotsSixVertical size={14} weight="bold" />
                    </div>
                    <Input placeholder="Label (e.g. Steel beams)" value={row.label}
                      onChange={(e) => updateRow(i, "label", e.target.value)}
                      className="col-span-3 h-8 text-sm" data-testid={`cost-edit-label-${i}`} />
                    <Input list="cost-edit-tag-suggestions" placeholder="Tag" value={row.tag}
                      onChange={(e) => updateRow(i, "tag", e.target.value)}
                      className="col-span-2 h-8 text-sm" data-testid={`cost-edit-tag-${i}`} />
                    <div className="col-span-2 relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs pointer-events-none">$</span>
                      <Input type="number" step="0.01" min="0" placeholder="0.00" value={row.amount}
                        onChange={(e) => updateRow(i, "amount", e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        className="pl-6 h-8 text-sm" data-testid={`cost-edit-amount-${i}`} />
                    </div>
                    <Input placeholder="Notes" value={row.notes}
                      onChange={(e) => updateRow(i, "notes", e.target.value)}
                      className="col-span-3 h-8 text-sm" data-testid={`cost-edit-notes-${i}`} />
                    <button
                      type="button" onClick={() => removeRow(i)}
                      className="col-span-1 h-8 flex items-center justify-center text-slate-400 hover:text-red-500"
                      title="Remove" data-testid={`cost-edit-remove-${i}`}>
                      <Trash size={13} />
                    </button>
                  </div>
                );
              })}
              <div className="flex items-center justify-end gap-2 pt-2 mt-1 border-t border-slate-200">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Costs:</span>
                <span className="text-sm font-bold text-slate-900" data-testid="cost-edit-total">
                  {new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(total)}
                </span>
              </div>
            </div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-primary text-white" data-testid="cost-edit-save-btn">
            {saving ? <ArrowsClockwise size={13} className="mr-1 animate-spin" /> : null}
            Save Changes
          </Button>
        </footer>
      </div>
    </div>
  );
}
